/**
 * Crea storefront chatbot — widget logic.
 * Vanilla ES6+, no bundler, no framework (per project conventions).
 *
 * Phase 2 scope:
 *   - Session ID + history persisted in localStorage
 *   - Open/close panel
 *   - Render messages (user + assistant)
 *   - Input handling (typing, Enter, click send)
 *   - SSE consumer streaming text deltas
 *   - Render product cards on `recommend_product` tool_use, Add-to-Cart via Shopify Cart API
 *   - Render email capture form on `capture_email` tool_use (UI only — submission is Phase 3)
 *   - Quick reply chips on initial open
 *   - Accessibility: focus trap, ARIA, ESC to close, body scroll lock on mobile
 *
 * NOT in Phase 2 (deferred to Phase 3+):
 *   - Submitting the email capture form
 *   - Cross-device persistence
 *   - Analytics events
 */

(() => {
  const SESSION_STORAGE_KEY = "fme_chat_session_id";
  const HISTORY_STORAGE_KEY = "fme_chat_history";
  const HISTORY_MAX = 10;

  const QUICK_REPLIES_EN = ["Energy", "Sleep", "Recovery", "Products"];
  const QUICK_REPLIES_ES = ["Energía", "Dormir", "Recuperación", "Productos"];

  // -- Top-level initialization ---------------------------------------------

  function init() {
    const root = document.getElementById("crea-chat-root");
    if (!root) return;

    const state = {
      root,
      endpoint: root.dataset.endpoint,
      accent: root.dataset.accent,
      greetingEn: root.dataset.greetingEn,
      greetingEs: root.dataset.greetingEs,
      products: safeParseJSON(root.dataset.products) || [],
      sessionId: ensureSessionId(),
      history: loadHistory(),
      open: false,
      streaming: false,
      pendingAssistant: null, // DOM li node currently receiving deltas
      lastFocusedBeforeOpen: null,
    };

    const els = {
      bubble: document.getElementById("crea-chat-bubble"),
      panel: document.getElementById("crea-chat-panel"),
      close: document.getElementById("crea-chat-close"),
      messages: document.getElementById("crea-chat-messages"),
      quickReplies: document.getElementById("crea-chat-quick-replies"),
      form: document.getElementById("crea-chat-form"),
      input: document.getElementById("crea-chat-input"),
      send: document.getElementById("crea-chat-send"),
    };

    bindEvents(state, els);
    hydrateFromHistory(state, els);
  }

  // -- State persistence ----------------------------------------------------

  function ensureSessionId() {
    let id = localStorage.getItem(SESSION_STORAGE_KEY);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(SESSION_STORAGE_KEY, id);
    }
    return id;
  }

  function loadHistory() {
    try {
      const raw = localStorage.getItem(HISTORY_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.slice(-HISTORY_MAX) : [];
    } catch {
      return [];
    }
  }

  function saveHistory(history) {
    try {
      localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(history.slice(-HISTORY_MAX)));
    } catch {
      /* localStorage can be unavailable (private mode, etc.) — best-effort */
    }
  }

  function safeParseJSON(str) {
    try {
      return JSON.parse(str);
    } catch {
      return null;
    }
  }

  // -- Event wiring ---------------------------------------------------------

  function bindEvents(state, els) {
    els.bubble.addEventListener("click", () => openPanel(state, els));
    els.close.addEventListener("click", () => closePanel(state, els));

    els.form.addEventListener("submit", (e) => {
      e.preventDefault();
      const text = els.input.value.trim();
      if (!text || state.streaming) return;
      sendMessage(state, els, text);
    });

    const syncSendDisabled = () => {
      els.send.disabled = !els.input.value.trim() || state.streaming;
    };
    els.input.addEventListener("input", syncSendDisabled);
    syncSendDisabled();

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && state.open) {
        closePanel(state, els);
      }
    });
  }

  // -- Panel open/close + focus management ----------------------------------

  let focusTrapHandler = null;

  function openPanel(state, els) {
    if (state.open) return;
    state.open = true;
    state.lastFocusedBeforeOpen = document.activeElement;
    els.panel.hidden = false;
    state.root.classList.add("crea-chat--open");
    document.body.classList.add("crea-chat-open");
    els.bubble.setAttribute("aria-expanded", "true");
    // User-initiated: tell other popups to yield (e.g. close an open countdown).
    if (window.FMEPopups) window.FMEPopups.markOpen("crea");

    focusTrapHandler = (e) => trapFocus(e, els.panel);
    document.addEventListener("keydown", focusTrapHandler);

    requestAnimationFrame(() => els.input.focus());

    if (els.messages.childElementCount === 0) {
      renderGreeting(state, els);
    } else {
      const lang = detectBrowserLanguage();
      const replies = lang === "es" ? QUICK_REPLIES_ES : QUICK_REPLIES_EN;
      renderQuickReplies(state, els, replies);
    }
  }

  function closePanel(state, els) {
    if (!state.open) return;
    state.open = false;
    els.panel.hidden = true;
    state.root.classList.remove("crea-chat--open");
    document.body.classList.remove("crea-chat-open");
    els.bubble.setAttribute("aria-expanded", "false");
    if (window.FMEPopups) window.FMEPopups.markClose("crea");

    if (focusTrapHandler) {
      document.removeEventListener("keydown", focusTrapHandler);
      focusTrapHandler = null;
    }

    if (state.lastFocusedBeforeOpen && state.lastFocusedBeforeOpen.focus) {
      state.lastFocusedBeforeOpen.focus();
    } else {
      els.bubble.focus();
    }
  }

  function trapFocus(e, panel) {
    if (e.key !== "Tab") return;
    const focusables = panel.querySelectorAll(
      'button, [href], input, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const visible = Array.from(focusables).filter((el) => !el.disabled && el.offsetParent !== null);
    if (visible.length === 0) return;
    const first = visible[0];
    const last = visible[visible.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  // -- Greeting + quick replies (first open only) ---------------------------

  function renderGreeting(state, els) {
    const lang = detectBrowserLanguage();
    const greeting = lang === "es" ? state.greetingEs : state.greetingEn;
    appendAssistantMessage(els, greeting);

    const replies = lang === "es" ? QUICK_REPLIES_ES : QUICK_REPLIES_EN;
    renderQuickReplies(state, els, replies);
  }

  function detectBrowserLanguage() {
    const lang = (navigator.language || "en").toLowerCase();
    return lang.startsWith("es") ? "es" : "en";
  }

  function renderQuickReplies(state, els, replies) {
    els.quickReplies.innerHTML = "";
    replies.forEach((label, i) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = `crea-chat__quick-reply crea-chat__quick-reply--v${i % 4}`;
      btn.textContent = label;
      btn.addEventListener("click", () => {
        clearQuickReplies(els);
        sendMessage(state, els, label);
      });
      els.quickReplies.appendChild(btn);
    });
    els.quickReplies.hidden = false;
  }

  function clearQuickReplies(els) {
    els.quickReplies.innerHTML = "";
    els.quickReplies.hidden = true;
  }

  // -- Message rendering ----------------------------------------------------

  function appendUserMessage(els, text) {
    const li = document.createElement("li");
    li.className = "crea-chat__message crea-chat__message--user";
    li.textContent = text;
    els.messages.appendChild(li);
    scrollToBottom(els);
    return li;
  }

  function appendAssistantMessage(els, text) {
    const li = document.createElement("li");
    li.className = "crea-chat__message crea-chat__message--assistant";
    li.textContent = text;
    els.messages.appendChild(li);
    scrollToBottom(els);
    return li;
  }

  function appendErrorMessage(els, text) {
    const li = document.createElement("li");
    li.className = "crea-chat__message crea-chat__message--error";
    li.textContent = text;
    els.messages.appendChild(li);
    scrollToBottom(els);
    return li;
  }

  function scrollToBottom(els) {
    els.messages.scrollTop = els.messages.scrollHeight;
  }

  // -- Re-hydrate from saved history on init --------------------------------

  function hydrateFromHistory(state, els) {
    state.history.forEach((m) => {
      if (m.role === "user") {
        appendUserMessage(els, m.content);
      } else if (m.role === "assistant") {
        appendAssistantMessage(els, m.content);
      }
    });
  }

  // -- Send message (stub — wired to backend in Task 4) ---------------------

  async function sendMessage(state, els, text) {
    // Drop any orphan user message that never got an assistant reply
    // (e.g. previous send failed, or browser closed mid-stream). Otherwise
    // the backend sees two consecutive user turns and replies to the wrong one.
    while (state.history.length && state.history[state.history.length - 1].role === "user") {
      state.history.pop();
    }
    saveHistory(state.history);

    appendUserMessage(els, text);
    state.history.push({ role: "user", content: text });
    saveHistory(state.history);
    els.input.value = "";
    setStreaming(state, els, true);

    // Create an empty assistant message that streaming deltas append to.
    state.pendingAssistant = appendAssistantMessage(els, "");
    const cursor = document.createElement("span");
    cursor.className = "crea-chat__cursor";
    cursor.setAttribute("aria-hidden", "true");
    state.pendingAssistant.appendChild(cursor);

    try {
      await streamChat(state, els, text);
    } catch (err) {
      if (state.pendingAssistant && state.pendingAssistant.textContent === "") {
        state.pendingAssistant.remove();
      }
      // Roll back the orphan user turn — otherwise it gets resent next call.
      if (state.history.length && state.history[state.history.length - 1].role === "user") {
        state.history.pop();
        saveHistory(state.history);
      }
      appendErrorMessage(els, "Crea is having trouble responding right now. Please try again in a moment.");
      console.error("[crea] streamChat error:", err);
    } finally {
      const li = state.pendingAssistant;
      if (li) {
        const cursorEl = li.querySelector(".crea-chat__cursor");
        if (cursorEl) cursorEl.remove();
        const finalText = li.textContent.trim();
        if (finalText) {
          state.history.push({ role: "assistant", content: finalText });
          saveHistory(state.history);
        } else {
          li.remove();
        }
      }
      state.pendingAssistant = null;
      setStreaming(state, els, false);
    }
  }

  function setStreaming(state, els, streaming) {
    state.streaming = streaming;
    els.input.disabled = streaming;
    els.send.disabled = streaming;
  }

  async function streamChat(state, els, message) {
    const payload = {
      session_id: state.sessionId,
      message,
      history: state.history.slice(0, -1).slice(-HISTORY_MAX),
    };

    const res = await fetch(state.endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!res.ok || !res.body) {
      throw new Error(`HTTP ${res.status}`);
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const events = parseSSEChunk(buffer);
      buffer = events.remainder;
      for (const evt of events.events) {
        handleSSEEvent(state, els, evt);
      }
    }

    // Flush any remaining buffered event
    if (buffer.trim()) {
      const tail = parseSSEChunk(buffer + "\n\n");
      for (const evt of tail.events) {
        handleSSEEvent(state, els, evt);
      }
    }
  }

  function parseSSEChunk(text) {
    // Events are separated by a blank line.
    const events = [];
    const parts = text.split(/\n\n/);
    const remainder = parts.pop() || "";
    for (const block of parts) {
      let event = "message";
      const dataLines = [];
      for (const line of block.split("\n")) {
        if (line.startsWith("event:")) {
          event = line.slice(6).trim();
        } else if (line.startsWith("data:")) {
          dataLines.push(line.slice(5).trim());
        }
      }
      const dataStr = dataLines.join("\n");
      let data = null;
      try { data = dataStr ? JSON.parse(dataStr) : null; } catch { data = dataStr; }
      events.push({ event, data });
    }
    return { events, remainder };
  }

  // Strip Markdown emphasis the prompt asks Crea to avoid. Keeps the
  // content, drops the syntax (so "**bold**" -> "bold").
  function stripMarkdown(text) {
    return text
      .replace(/\*\*(.+?)\*\*/g, "$1")
      .replace(/__(.+?)__/g, "$1")
      .replace(/(^|[\s(])\*(?!\s)(.+?)\*(?=[\s).,!?:;]|$)/g, "$1$2")
      .replace(/(^|[\s(])_(?!\s)(.+?)_(?=[\s).,!?:;]|$)/g, "$1$2")
      .replace(/^\s*[-*+]\s+/gm, "")
      .replace(/^\s*\d+\.\s+/gm, "");
  }

  function handleSSEEvent(state, els, evt) {
    if (!evt) return;
    if (evt.event === "text" && evt.data && typeof evt.data.delta === "string") {
      const li = state.pendingAssistant;
      if (!li) return;
      const prevRaw = li.dataset.rawText || "";
      const rawText = prevRaw + evt.data.delta;
      li.dataset.rawText = rawText;
      const cursorEl = li.querySelector(".crea-chat__cursor");
      // Re-render cleaned text and re-attach cursor.
      li.textContent = stripMarkdown(rawText);
      if (cursorEl) li.appendChild(cursorEl);
      scrollToBottom(els);
    } else if (evt.event === "tool_use") {
      handleToolUse(state, els, evt.data);
    } else if (evt.event === "done") {
      // No-op — finalization happens in sendMessage's finally block.
    } else if (evt.event === "error") {
      throw new Error((evt.data && evt.data.message) || "stream error");
    }
  }

  function handleToolUse(state, els, data) {
    if (!data || !data.name) return;
    if (data.name === "recommend_product") {
      renderProductCard(state, els, data.input || {});
    } else if (data.name === "capture_email") {
      renderEmailCapture(state, els, data.input || {});
    }
  }

  function renderProductCard(state, els, input) {
    const handle = input.product_handle;
    const product = state.products.find((p) => p.handle === handle);
    if (!product) {
      console.warn("[crea] product card requested for unknown handle:", handle);
      return;
    }

    const li = state.pendingAssistant;
    if (!li) return;

    const card = document.createElement("div");
    card.className = "crea-chat__product-card";

    if (product.image) {
      const img = document.createElement("img");
      img.className = "crea-chat__product-card-image";
      img.src = product.image;
      img.alt = product.title || "";
      img.loading = "lazy";
      card.appendChild(img);
    }

    const body = document.createElement("div");
    body.className = "crea-chat__product-card-body";

    const title = document.createElement("p");
    title.className = "crea-chat__product-card-title";
    const link = document.createElement("a");
    link.href = product.url;
    link.textContent = product.title;
    link.style.color = "inherit";
    link.style.textDecoration = "none";
    title.appendChild(link);

    const price = document.createElement("p");
    price.className = "crea-chat__product-card-price";
    price.textContent = product.price;

    body.appendChild(title);
    body.appendChild(price);
    card.appendChild(body);

    const cta = document.createElement("button");
    cta.type = "button";
    cta.className = "crea-chat__product-card-cta";
    cta.textContent = "Add to Cart";
    cta.disabled = !product.available;
    if (!product.available) {
      cta.textContent = "Sold out";
    }
    cta.addEventListener("click", () => addToCart(cta, product));
    card.appendChild(cta);

    li.appendChild(card);
    scrollToBottom(els);
  }

  async function addToCart(button, product) {
    if (!product.variant_id) {
      console.warn("[crea] product has no variant_id:", product);
      return;
    }
    const original = button.textContent;
    button.disabled = true;
    button.textContent = "Adding...";
    try {
      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: product.variant_id, quantity: 1 }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      button.textContent = "Added ✓";
      // Notify Dawn's cart drawer if present (theme-side listener)
      document.dispatchEvent(new CustomEvent("crea:cart:added", { detail: { variant_id: product.variant_id } }));
      setTimeout(() => {
        button.textContent = original;
        button.disabled = false;
      }, 2000);
    } catch (err) {
      console.error("[crea] addToCart failed:", err);
      button.textContent = "Error — try page";
      setTimeout(() => {
        button.textContent = original;
        button.disabled = false;
      }, 2500);
    }
  }

  function renderEmailCapture(state, els, input) {
    const li = state.pendingAssistant;
    if (!li) return;

    const wrap = document.createElement("div");
    wrap.className = "crea-chat__email-capture";

    if (input.message) {
      const p = document.createElement("p");
      p.className = "crea-chat__email-capture-message";
      p.textContent = input.message;
      wrap.appendChild(p);
    }

    const row = document.createElement("div");
    row.className = "crea-chat__email-capture-row";

    const inputEl = document.createElement("input");
    inputEl.type = "email";
    inputEl.className = "crea-chat__email-capture-input";
    inputEl.placeholder = "you@example.com";
    inputEl.required = true;

    const submit = document.createElement("button");
    submit.type = "button";
    submit.className = "crea-chat__email-capture-submit";
    submit.textContent = "Send me the code";
    submit.addEventListener("click", () => {
      // Phase 3 will POST to /functions/v1/storefront-chat/email here.
      // For now, just acknowledge the UI flow in the console.
      const email = inputEl.value.trim();
      if (!email) return;
      submit.disabled = true;
      submit.textContent = "Thanks! (Phase 3 wires this up)";
      inputEl.disabled = true;
      console.log("[crea] email capture (phase 2 stub):", { session_id: state.sessionId, email, offer_type: input.offer_type });
    });

    row.appendChild(inputEl);
    row.appendChild(submit);
    wrap.appendChild(row);

    li.appendChild(wrap);
    scrollToBottom(els);
  }

  // -- Boot -----------------------------------------------------------------

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
